import { toast } from "react-toastify";

// Keep this URL in one place so all API requests use the same base URL.
export const baseURL = "https://crm.bizconvo.in/be/api/";

type RequestConfig = {
  url: string;
  method: string;
  authorization?: boolean;
};

type RequestData = {
  bodyData?: unknown;
};

class ApiRequestError extends Error {
  status?: number;
  code: string;

  constructor(message: string, code: string, status?: number) {
    super(message);
    this.name = "ApiRequestError";
    this.code = code;
    this.status = status;
  }
}

const showApiError = (error: ApiRequestError) => {
  // Stable toast IDs prevent a toast storm when several widgets fail together.
  if (error.status === 503 || error.code === "SERVICE_UNAVAILABLE") {
    toast.error(
      "We're temporarily busy processing messages. Your screen data has been kept where possible. Please try refreshing in a moment.",
      { toastId: "api-service-unavailable", autoClose: 6000 }
    );
    return;
  }

  if (error.code === "NETWORK_ERROR") {
    toast.error(
      "We couldn't connect to the server. Please check your connection and try again.",
      { toastId: "api-network-error", autoClose: 6000 }
    );
    return;
  }

  if (error.status && error.status >= 500) {
    toast.error(
      "Something went wrong on our server. Your existing screen data has not been intentionally cleared. Please try again shortly.",
      { toastId: "api-server-error", autoClose: 6000 }
    );
    return;
  }

  if (error.code === "INVALID_RESPONSE") {
    toast.error(
      "The server sent an unexpected response. Please try again shortly.",
      { toastId: "api-invalid-response", autoClose: 6000 }
    );
  }
};

const API = async (requestURL: RequestConfig, requestAPIData: RequestData = {}) => {
  const endpoint = baseURL + requestURL.url;
  const headers: Record<string, string> = { Accept: "application/json" };

  if (requestURL.authorization) {
    const path = window.location.pathname;
    let token = "";

    if (path.startsWith("/vendor")) {
      token = sessionStorage.getItem("vendorToken") || "";
    } else if (path.startsWith("/super-admin")) {
      token = localStorage.getItem("superAdminToken") || "";
    }

    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const method = (requestURL.method || "GET").toUpperCase();
  const requestInit: RequestInit = { method, headers };

  if (method !== "GET" && method !== "DELETE") {
    if (requestAPIData.bodyData instanceof FormData) {
      // Do not set Content-Type for FormData; the browser must add its boundary.
      requestInit.body = requestAPIData.bodyData;
    } else if (requestAPIData.bodyData !== undefined) {
      headers["Content-Type"] = "application/json";
      requestInit.body = JSON.stringify(requestAPIData.bodyData);
    }
  }

  let response: Response;
  try {
    response = await fetch(endpoint, requestInit);
  } catch (cause) {
    const error = new ApiRequestError(
      "Unable to connect to the API server.",
      "NETWORK_ERROR"
    );
    showApiError(error);
    // Preserve normal promise rejection so existing page-level finally/catch logic runs.
    throw error;
  }

  const contentType = response.headers.get("Content-Type") || "";
  if (contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")) {
    if (!response.ok) {
      const error = new ApiRequestError(
        "The server could not complete this request.",
        response.status === 503 ? "SERVICE_UNAVAILABLE" : "HTTP_ERROR",
        response.status
      );
      showApiError(error);
      throw error;
    }
    return response.blob();
  }

  let responseData: any;
  const rawBody = await response.text();
  if (rawBody.trim()) {
    try {
      responseData = JSON.parse(rawBody);
    } catch {
      if (response.status >= 500 || response.ok) {
        const error = new ApiRequestError(
          "The server returned an unreadable response.",
          "INVALID_RESPONSE",
          response.status
        );
        showApiError(error);
        throw error;
      }
      responseData = {
        apiStatus: {
          code: String(response.status),
          message: "The request could not be completed. Please check your input and try again.",
        },
      };
    }
  } else {
    responseData = {};
  }

  // Server/infrastructure failures are surfaced consistently across the app.
  if (response.status >= 500) {
    const error = new ApiRequestError(
      response.status === 503
        ? "Service temporarily unavailable."
        : "The server could not complete this request.",
      response.status === 503 ? "SERVICE_UNAVAILABLE" : "HTTP_ERROR",
      response.status
    );
    showApiError(error);
    throw error;
  }

  // Keep the existing API response shape for business errors (e.g. validation/404).
  if (!response.ok && !responseData.apiStatus) {
    responseData.apiStatus = {
      code: String(response.status),
      message: "The request could not be completed. Please try again.",
    };
  }

  return responseData;
};

export default API;
